import type { ApiListingRequest, ApiResponse } from '@bitstopco/bitstop-theme';

import { locallyPaginatedData } from '@/helpers/api';

import api from '@/store/api';
import { athena, ergot } from '@/store/api/instances';

import type {
  NonceRole,
  PostCreateUserRequest,
  PostCreateUserV2Request,
  PutEditRoleRequest,
  PutEditRoleV2Request,
  Role,
  User,
} from '@/types';

/**
 * Users RTK-Query API
 * For more information about RTK-Query see
 * * https://redux-toolkit.js.org/rtk-query/overview
 */
interface GetUsersRequest {
  role_id?: number;
  search_text?: string;
  pagination?: {
    current_page?: number;
    items_per_page?: number;
    sort_by?: string;
    sort_ascending?: boolean;
  };
}

export const usersApi = api.injectEndpoints({
  endpoints: (build) => ({
    getUsers: build.query<User[], void>({
      query: () =>
        ergot({
          url: 'users',
        }),
      providesTags: ['Users'],
    }),
    getUsersAthena: build.query<ApiResponse<User[]>, GetUsersRequest | void>({
      query: (params) => {
        const url = params?.role_id ? `v2/Users?roleId=${params.role_id}` : 'v2/Users';
        const data: any = {
          pagination: params?.pagination || {
            current_page: 1,
            items_per_page: 1000,
            sort_by: 'first_name',
            sort_ascending: true,
          },
        };

        // Add search_text if provided
        if (params?.search_text) {
          data.search_text = params.search_text;
        }

        return athena({
          url,
          method: 'POST',
          data,
        });
      },
      providesTags: ['Users'],
    }),
    getUser: build.query<User, number>({
      query: (id) =>
        athena({
          url: `v2/Users/${id}`,
        }),
      transformResponse: (response: ApiResponse<User>) => response.data,
      providesTags: (_, __, id) => [{ type: 'Users', id }, { type: 'Users' }],
    }),
    getUserRoles: build.query<Role[], void>({
      query: () =>
        ergot({
          url: 'roles',
        }),
      providesTags: ['Roles'],
    }),
    putEditUserRole: build.mutation<void, PutEditRoleRequest>({
      query: (data) =>
        ergot({
          data,
          method: 'PUT',
          url: 'users/roles',
          showError: true,
        }),
      invalidatesTags: ['Users'],
    }),
    postCreateUser: build.mutation<void, PostCreateUserRequest>({
      query: (data) =>
        ergot({
          data,
          method: 'POST',
          url: 'users',
          showError: true,
        }),
      invalidatesTags: ['Users'],
    }),
    getNonceUsers: build.query<ApiResponse<User[]>, ApiListingRequest>({
      query: (data) =>
        athena({
          data,
          method: 'POST',
          url: 'v2/Users/nonce',
        }),
      providesTags: ['Users'],
    }),
    getNonceRoles: build.query<NonceRole[], void>({
      query: () =>
        athena({
          url: 'v2/Roles/nonce',
        }),
      transformResponse: (response: ApiResponse<NonceRole[]>) => response.data,
      providesTags: ['Roles'],
    }),
    putEditUserRoleV2: build.mutation<void, PutEditRoleV2Request>({
      query: ({ id, role_id }) =>
        athena({
          data: { role_id },
          method: 'PUT',
          url: `v2/UserAdministration/${id}/role`,
          showError: true,
        }),
      invalidatesTags: ['Users'],
    }),
    postCreateUserV2: build.mutation<void, PostCreateUserV2Request>({
      query: (data) =>
        athena({
          data,
          method: 'POST',
          url: 'v2/UserAdministration',
          showError: true,
        }),
      invalidatesTags: ['Users'],
    }),
    postResetUserPasswordV2: build.mutation<void, number>({
      query: (id) =>
        athena({
          method: 'POST',
          url: `v2/UserAdministration/${id}/password-reset`,
          showError: true,
        }),
    }),
  }),
});

export const {
  // Queries
  useGetUsersQuery,
  useGetUsersAthenaQuery,
  useGetUserQuery,
  useGetUserRolesQuery,
  useGetNonceUsersQuery,
  useGetNonceRolesQuery,

  // Mutations
  usePutEditUserRoleMutation,
  usePutEditUserRoleV2Mutation,
  usePostCreateUserMutation,
  usePostCreateUserV2Mutation,
  usePostResetUserPasswordV2Mutation,
} = usersApi;

export const {
  endpoints: { getUsers, getUser, getUserRoles, putEditUserRole, postCreateUser },
} = usersApi;

export const useGetPaginatedUsers = (payload?: ApiListingRequest) => {
  const { data: users = [], isFetching } = useGetUsersQuery(undefined, { skip: !payload });

  const data = locallyPaginatedData(users, payload);

  return {
    data,
    isFetching,
  };
};
